import { createSlice } from "@reduxjs/toolkit";
import {
  CFG_ALGORITHM,
  CFG_ASSIGNMENTS,
  CFG_CHOICE_NAMES,
  CFG_CONSTRAINTS,
  CFG_FILTERS,
  CFG_FILTERS_TARGET,
  CFG_FILTERS_TYPE,
  CFG_FILTERS_VALUE,
  CFG_HEADER,
  CFG_NWISE_COVERAGE,
  CFG_NWISE_N,
  CFG_NWISE_TUPLES,
  CFG_RND_ADAPTIVE,
  CFG_RND_DUPLICATES,
  CFG_RND_LENGTH,
  CFG_SEPARATOR,
  CFG_TUPLES,
} from "../../names/GeneratorNames";

const initialState = {
  showPageGenerator: false,
  config: {
    [CFG_ALGORITHM]: "n-wise",
    [CFG_NWISE_N]: 2,
    [CFG_NWISE_COVERAGE]: 100,
    [CFG_NWISE_TUPLES]: "",
    [CFG_RND_LENGTH]: 5,
    [CFG_RND_DUPLICATES]: "False",
    [CFG_RND_ADAPTIVE]: "False",
    [CFG_CONSTRAINTS]: "Process constraints normally",
    [CFG_ASSIGNMENTS]: "Process assignments normally",
    [CFG_FILTERS]: [
      {
        [CFG_FILTERS_TYPE]: "Blacklist",
        [CFG_FILTERS_TARGET]: "",
        [CFG_FILTERS_VALUE]: "",
      },
    ],
    [CFG_HEADER]: true,
    [CFG_CHOICE_NAMES]: false,
    [CFG_SEPARATOR]: ",",
  },
  data: [],
  errors: [],
  progress: {
    done: 0,
    total: 0,
    percentage: 0,
  },
  sortConfig: {
    column: null,
    direction: 'asc',
  },
};

//-----------------------------------------------------------------------------------

const generatorSlice = createSlice({
  name: "generator",
  initialState,
  reducers: {
    setShowPageGenerator(state, action) {
      state.showPageGenerator = action.payload;
    },
    addData(state, action) {
      state.data = [...state.data, ...action.payload];
    },
    addErrors(state, action) {
      state.errors = [...state.errors, ...action.payload];
    },
    setProgress(state, action) {
      state.progress = action.payload;
    },
    resetData(state, action) {
      state.data = [];
      state.errors = [];
      state.progress = {
        done: 0,
        total: 0,
        percentage: 0,
      }
    },
    resetGenerator(state, action) {
      state.config = initialState.config;
    },
    addGeneratorFilter(state, action) {
      state.config = {
        ...state.config,
        [CFG_FILTERS]: [
          ...state.config[CFG_FILTERS],
          {
            [CFG_FILTERS_TYPE]: "",
            [CFG_FILTERS_TARGET]: "",
            [CFG_FILTERS_VALUE]: "",
          },
        ],
      };
    },
    removeGeneratorFilter(state, action) {
      state.config = {
        ...state.config,
        [CFG_FILTERS]: state.config[CFG_FILTERS].filter((_, i) => i !== action.payload),
      };
    },
    modifyGeneratorData: {
      prepare(config = {}) {
        return {
          payload: { name: config.name, value: config.value, index: config.index },
        };
      },
      reducer(state, action) {
        
        if (action.payload.index === -1) {
          state.config = { ...state.config, [action.payload.name]: action.payload.value };
        } else {
          const updatedFilters = [...state.config[CFG_FILTERS]];
          if (action.payload.name === CFG_TUPLES) {
            updatedFilters[action.payload.index] = {
              ...updatedFilters[action.payload.index],
              value: action.payload.value,
            };
          } else {
            updatedFilters[action.payload.index] = {
              ...updatedFilters[action.payload.index],
              [action.payload.name]: action.payload.value,
            };
          }
          state.config = { ...state.config, [CFG_FILTERS]: updatedFilters };
        }
      },
    },
    sortData: {
      prepare(columnIndex, direction) {
        return {
          payload: { columnIndex, direction },
        };
      },
      reducer(state, action) {
        const { columnIndex, direction } = action.payload;

        state.sortConfig = {
          column: columnIndex,
          direction: direction,
        }

        if (state.data.length > 0) {
          const separator = state.config[CFG_SEPARATOR] || ',';
          const hasHeader = state.config[CFG_HEADER];
          
          if (hasHeader && state.data.length > 1) {
            // Keep header row separate and only sort data rows
            const headerRow = state.data[0];
            const dataRows = state.data.slice(1);
            
            dataRows.sort((a, b) => {
              const aValue = a.split(separator)[columnIndex] || '';
              const bValue = b.split(separator)[columnIndex] || '';

              const aNum = parseFloat(aValue);
              const bNum = parseFloat(bValue);

              let comparison = 0;

              if (!isNaN(aNum) && !isNaN(bNum)) {
                comparison = aNum - bNum;
              } else {
                comparison = aValue.localeCompare(bValue);
              }

              return direction === 'desc' ? -comparison : comparison;
            });
            
            // Reconstruct the array with header first, then sorted data
            state.data = [headerRow, ...dataRows];
          } else {
            // No header, sort all data
            state.data.sort((a, b) => {
              const aValue = a.split(separator)[columnIndex] || '';
              const bValue = b.split(separator)[columnIndex] || '';

              const aNum = parseFloat(aValue);
              const bNum = parseFloat(bValue);

              let comparison = 0;

              if (!isNaN(aNum) && !isNaN(bNum)) {
                comparison = aNum - bNum;
              } else {
                comparison = aValue.localeCompare(bValue);
              }

              return direction === 'desc' ? -comparison : comparison;
            });
          }
        }
      },
    },
  },
});

//-----------------------------------------------------------------------------------

export const getShowPageGenerator = (state) => {
  return state.generator.showPageGenerator;
};

export const getGeneratorData = (state) => {
  return state.generator.config;
};

export const getData = (state) => {
  return state.generator.data;
};

export const getErrors = (state) => {
  return state.generator.errors;
};

export const getSortConfig = (state) => {
  return state.generator.sortConfig;
};
export const getProgress = (state) => {
  return state.generator.progress;
};

//-----------------------------------------------------------------------------------

export default generatorSlice.reducer;
export const {
  addData,
  addErrors,
  resetData,
  setProgress,
  setShowPageGenerator,
  resetGenerator,
  addGeneratorFilter,
  removeGeneratorFilter,
  modifyGeneratorData,
  sortData,
} = generatorSlice.actions;
